/**
 * Sentry error monitoring — frontend.
 *
 * Imported first in main.jsx so Sentry.init runs before any React code mounts.
 *
 * DSN comes from VITE_SENTRY_DSN env var. If not set, Sentry is silently
 * disabled — the rest of the app runs normally. See DEPLOYMENT_NOTES.md for
 * Vercel env-var configuration.
 */

import * as Sentry from "@sentry/react";
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.MODE || "development";

export function initSentry() {
  if (!SENTRY_DSN) {
    console.log("[Sentry] Disabled (VITE_SENTRY_DSN not set)");
    return;
  }

  // Release tag — Vercel injects VITE_VERCEL_GIT_COMMIT_SHA on every
  // deploy. Same purpose as the backend's RENDER_GIT_COMMIT: every
  // Sentry event groups by deploy SHA so regressions surface fast
  // ("this error class first appeared in commit abc123") and so
  // session-health metrics can compare release vs. release.
  const release =
    import.meta.env.VITE_SENTRY_RELEASE ||
    import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ||
    undefined;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release,

    integrations: [
      // Automatic React Router v6 page-load/navigation transaction tracking.
      // Each route change gets a parameterized name like "/realestate/clients/:clientId"
      // instead of the concrete URL with IDs, so similar routes aggregate.
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // Performance tracing: 10% in production keeps quota usage low.
    tracesSampleRate: ENVIRONMENT === "production" ? 0.1 : 1.0,

    // Only forward errors to Sentry if they happen to requests going to our
    // own backend or same-origin. Blocks noise from Stripe.js, Twilio,
    // Intercom, ad blockers injecting into random pages.
    tracePropagationTargets: [
      /^\//, // same-origin (Vercel)
      /^https:\/\/stacked-backend-gv43\.onrender\.com/,
    ],

    // Don't send default PII (IPs, cookies, auth headers). We set user
    // context explicitly in AuthContext — that's a controlled, minimal payload.
    sendDefaultPii: false,

    // Filter: drop noise before it hits Sentry's quota.
    beforeSend(event, hint) {
      const err = hint.originalException;
      const message = err?.message || event.message || "";

      // 401s are auth flow, not bugs. The app handles them by redirecting to login.
      if (message.includes("401") || message.includes("Unauthorized")) {
        return null;
      }

      // Expected fetch cancellations when users navigate away mid-request.
      if (
        err?.name === "AbortError" ||
        message.includes("The user aborted a request") ||
        message.includes("Failed to fetch") ||
        message.includes("NetworkError") ||
        message.includes("Load failed")
      ) {
        return null;
      }

      // ResizeObserver loop warnings — browser quirk, not a real error.
      if (
        message.includes("ResizeObserver loop") ||
        message.includes("ResizeObserver loop completed")
      ) {
        return null;
      }

      // Stripe.js script errors from ad blockers / extensions / network blocks.
      // Stripe.js loads from js.stripe.com; ad blockers (uBlock, Privacy Badger,
      // corporate proxies) and certain VPNs block that domain, which surfaces as
      // a "Failed to load Stripe.js" runtime error. There is nothing we can do
      // from our end — those errors are user-environment noise, not bugs.
      const lowerMessage = message.toLowerCase();
      if (
        err?.name === "ScriptError" ||
        lowerMessage.includes("stripe") ||
        lowerMessage.includes("failed to load stripe")
      ) {
        return null;
      }

      // Read-only property mutation errors caused by browser extensions.
      // Some extensions (form-fillers, password managers, accessibility tools)
      // iterate over <option>/<input>/<select> elements and try to copy their
      // properties via Object.assign, which throws a TypeError on readonly DOM
      // properties like HTMLOptionElement.defaultSelected. We don't write to
      // these properties anywhere in our code (verified by grep), so any such
      // error is third-party noise.
      if (
        message.includes("Cannot assign to read only property") ||
        message.includes("which has only a getter")
      ) {
        return null;
      }

      // Third-party extensions injecting scripts into the page.
      if (
        message.includes("chrome-extension://") ||
        message.includes("moz-extension://") ||
        message.includes("safari-extension://")
      ) {
        return null;
      }

      // ── Scrub URLs ──────────────────────────────────────────────────
      // Some of our URLs carry tokens in the query string (?code=…
      // OAuth callbacks, eSignature signing links, etc). Strip query
      // strings on the request URL and breadcrumb URLs so secrets don't
      // land in Sentry. The path itself is preserved for diagnostic value.
      const stripQuery = (url) => {
        if (typeof url !== "string") return url;
        const q = url.indexOf("?");
        return q === -1 ? url : url.slice(0, q);
      };
      if (event.request?.url) {
        event.request.url = stripQuery(event.request.url);
      }
      if (Array.isArray(event.breadcrumbs)) {
        for (const b of event.breadcrumbs) {
          if (b?.data?.url) b.data.url = stripQuery(b.data.url);
          if (b?.data?.to) b.data.to = stripQuery(b.data.to);
          if (b?.data?.from) b.data.from = stripQuery(b.data.from);
        }
      }

      // ── Scrub secret-looking substrings out of error messages ──────
      // Catches cases where an HTTP error includes the URL/body in its
      // message (RTK Query does this), or where a thrown Error has an
      // access token baked into it.
      const scrubText = (s) => {
        if (typeof s !== "string") return s;
        return s
          .replace(/Bearer\s+[A-Za-z0-9._-]+/g, "Bearer [REDACTED]")
          .replace(/\b[a-f0-9]{32,}\b/g, "[REDACTED-HEX]")
          .replace(/\bsk_[A-Za-z0-9_]{16,}\b/g, "[REDACTED-STRIPE]")
          .replace(/\bwh_[A-Za-z0-9_]{16,}\b/g, "[REDACTED-WEBHOOK]");
      };
      if (event.message) event.message = scrubText(event.message);
      if (event.exception?.values) {
        for (const ex of event.exception.values) {
          if (ex?.value) ex.value = scrubText(ex.value);
        }
      }

      // Belt + suspenders: even though setSentryUser omits email, drop
      // it here if some other code path slipped one in.
      if (event.user?.email) delete event.user.email;
      if (event.user?.ip_address) delete event.user.ip_address;

      return event;
    },
  });

  console.log(`[Sentry] Initialized in ${ENVIRONMENT} mode`);
}

/**
 * Call after a successful login. Tags subsequent Sentry events with the
 * user's identity so errors can be traced back to who hit them.
 *
 * Deliberately omits email: Sentry's sendDefaultPii=false does NOT apply
 * to user fields you set explicitly via setUser — anything we pass here
 * is sent verbatim. The Mongo _id + role is enough to correlate a
 * Sentry event back to an account via our own database; there's no
 * reason to put PII in a third-party tracker.
 */
export function setSentryUser(user) {
  if (!SENTRY_DSN || !user) return;
  Sentry.setUser({
    id: user._id || user.id,
    role: user.role,
  });
}

/**
 * Call on logout. Clears user identity from Sentry scope so subsequent
 * anonymous errors (e.g., on the login page) don't get tagged to the
 * previously logged-in user.
 */
export function clearSentryUser() {
  if (!SENTRY_DSN) return;
  Sentry.setUser(null);
}

/**
 * Explicit capture helper for caught errors that the app recovers from but
 * still wants to report (e.g., the ErrorBoundary showing a fallback UI).
 */
export function captureException(error, context) {
  if (!SENTRY_DSN) {
    console.error("[captureException]", error, context);
    return;
  }
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

export default Sentry;
